import { ScheduledBlock } from 'types/ScheduledBlock';
import { getBlockId } from './getBlockId';

/**
 * The kind of block that owns a band of the drag-start geometry. Targeting rules
 * are written against these: pointing at the active block's own band reverts the
 * preview, an appointment band holds the current preview, anything else is a
 * drop target.
 */
export type SlotBandOwnerType = 'active' | 'appointment' | 'movable' | 'empty';

/**
 * A vertical band of the schedule as it was rendered when the drag started,
 * mapped to the block that owned it and that block's slot offset.
 */
export interface SlotBand {
  top: number;
  bottom: number;
  ownerId: string;
  ownerType: SlotBandOwnerType;

  /**
   * The slot offset (minutes from the schedule start) a pointer in this band
   * targets: the owning block's start offset.
   */
  offset: number;
}

/**
 * Builds the drag-start slot geometry: one band per rendered block, from the
 * droppable rects dnd-kit measured at drag start joined with the origin block
 * list. The bands — not live droppables — are the single source of drop
 * targeting for the whole drag: the preview may re-tile, unmount, or re-order
 * blocks freely without ever perturbing what the pointer resolves to.
 */
export function captureSlotGeometry(
  droppableRects: Map<string, { top: number; bottom: number }>,
  originBlocks: ScheduledBlock[],
  activeId: string,
): SlotBand[] {
  const bands: SlotBand[] = [];

  for (const block of originBlocks) {
    const id = getBlockId(block);
    const rect = droppableRects.get(id);

    if (!rect) {
      continue;
    }

    const ownerType: SlotBandOwnerType =
      id === activeId ? 'active'
        : block.intent?.todo?.type === 'appointment' ? 'appointment'
          : block.intent ? 'movable' : 'empty';

    bands.push({
      top: rect.top,
      bottom: rect.bottom,
      ownerId: id,
      ownerType,
      offset: block.startOffset,
    });
  }

  return bands.sort((a, b) => a.top - b.top);
}
